import { Injector } from '@angular/core';
import { FormActionTypeEnum } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  DocumentMonthsUpdate,
  DocumentStateContext,
  DownloadDocumentType,
  DownloadDocumentTypeKey,
  PaymentProvider,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import dayjs from 'dayjs';
import { Observable, take } from 'rxjs';
import { SetFormActionWithId } from '../../../actions/form-submission.action';
import { GetAvailableSavingsProducts } from '../../../actions/savings-account.action';
import { CtaButtonSignalService } from '../../../services/cta-button-signal-service/cta-button-signal.service';
import { DownloadDocumentService } from '../../../services/download-document/download-document.service';
import { AccountState } from '../../../state/account.state';
import { FormActionState } from '../../../state/form-submision.state';
import { NotificationState } from '../../../state/notifications.state';
import { TransactionState } from '../../../state/transaction.state';
import { ProjectSettingsState } from '../../../state/project-settings.state';
import { SavingsAccountState } from '../../../state/savings-account.state';
import { SavingsProductsState } from '../../../state/savings-products.state';
import { AvailableSavingsProductType } from '../../../services/savings-account-service/savings-account.type';

interface TransactionData {
  id: string;
  type: string;
}

const isStatementType = (
  type: string,
): type is
  | DownloadDocumentType.BANK_STATEMENT
  | DownloadDocumentType.HOLDING_STATEMENT
  | DownloadDocumentType.SAVINGS_BANK_STATEMENT
  | DownloadDocumentType.SAVINGS_ANNUAL_STATEMENT =>
  (
    [
      DownloadDocumentType.BANK_STATEMENT,
      DownloadDocumentType.HOLDING_STATEMENT,
      DownloadDocumentType.SAVINGS_BANK_STATEMENT,
      DownloadDocumentType.SAVINGS_ANNUAL_STATEMENT,
    ] as string[]
  ).includes(type);

export const downloadDocumentFile = (
  injector: Injector,
  widgetAttributes: UiZoneWidgetAttributeConfig,
): void => {
  const store = injector.get(Store);
  const accountId = store.selectSnapshot(AccountState.getCurrentAccountId);
  const flutterWebView = (window as unknown as Record<string, unknown>)['flutter_inappwebview'] || null;
  const widgetProps = widgetAttributes.widgetProperties;
  const documentType = widgetProps?.['documentType'];
  const timeInterval = widgetProps?.['timeInterval'];
  const downloadDocumentAs = widgetProps?.['downloadDocumentAs'];
  const CTAButtonKey = widgetProps['buttonActionType'] as string;

  if (documentType === DownloadDocumentType.SAVINGS_KEY_PRODUCT_INFORMATION) {
    handleKeyProductInformation(injector, downloadDocumentAs, flutterWebView, CTAButtonKey);
    return;
  }

  if (timeInterval !== DocumentMonthsUpdate.USER_DEFINED && isStatementType(documentType)) {
    handleTimeIntervalBankStatement(
      injector,
      widgetAttributes,
      store,
      documentType,
      accountId,
      downloadDocumentAs,
      flutterWebView,
      CTAButtonKey,
    );
    return;
  }

  if (timeInterval === DocumentMonthsUpdate.USER_DEFINED) {
    handleManualDateSelection(
      injector,
      widgetAttributes,
      store,
      documentType,
      accountId,
      downloadDocumentAs,
      flutterWebView,
      CTAButtonKey,
    );
  }
};

const getDocumentTemplateType = (
  documentType: DownloadDocumentType,
  downloadDocumentAs: string,
): DownloadDocumentTypeKey => {
  const templateMap: Record<
    DownloadDocumentType,
    { pdf: DownloadDocumentTypeKey; csv?: DownloadDocumentTypeKey }
  > = {
    [DownloadDocumentType.BANK_STATEMENT]: {
      csv: DownloadDocumentTypeKey.BANK_STATEMENT_CSV,
      pdf: DownloadDocumentTypeKey.BANK_STATEMENT_PDF,
    },
    [DownloadDocumentType.SAVINGS_BANK_STATEMENT]: {
      csv: DownloadDocumentTypeKey.SAVINGS_BANK_STATEMENT_CSV,
      pdf: DownloadDocumentTypeKey.SAVINGS_BANK_STATEMENT_PDF,
    },
    [DownloadDocumentType.SAVINGS_ANNUAL_STATEMENT]: {
      pdf: DownloadDocumentTypeKey.SAVINGS_ANNUAL_STATEMENT_PDF,
    },
    [DownloadDocumentType.SAVINGS_KEY_PRODUCT_INFORMATION]: {
      pdf: DownloadDocumentTypeKey.SAVINGS_KEY_PRODUCT_INFORMATION_PDF,
    },
    [DownloadDocumentType.HOLDING_STATEMENT]: {
      pdf: DownloadDocumentTypeKey.HOLDING_STATEMENT_PDF,
    },
    [DownloadDocumentType.PROOF_OF_PAYMENT]: {
      csv: DownloadDocumentTypeKey.PROOF_OF_PAYMENT_CSV,
      pdf: DownloadDocumentTypeKey.PROOF_OF_PAYMENT_PDF,
    },
    [DownloadDocumentType.PROOF_OF_EXCHANGE]: {
      csv: DownloadDocumentTypeKey.PROOF_OF_EXCHANGE_CSV,
      pdf: DownloadDocumentTypeKey.PROOF_OF_EXCHANGE_PDF,
    },
  };

  const templates = templateMap[documentType];
  if (templates?.csv && templates.csv.includes(downloadDocumentAs)) {
    return templates.csv;
  }

  return templates.pdf;
};

const handleTimeIntervalBankStatement = (
  injector: Injector,
  widgetAttributes: UiZoneWidgetAttributeConfig,
  store: Store,
  documentType:
    | DownloadDocumentType.BANK_STATEMENT
    | DownloadDocumentType.HOLDING_STATEMENT
    | DownloadDocumentType.SAVINGS_BANK_STATEMENT
    | DownloadDocumentType.SAVINGS_ANNUAL_STATEMENT,
  accountId: string,
  downloadDocumentAs: string,
  flutterWebView: unknown,
  CTAButtonKey: string,
): void => {
  const { startDate, endDate } = calculateAndSetDateRange(
    widgetAttributes.widgetProperties?.['timeInterval'],
    store,
  );
  const documentTemplateType = getDocumentTemplateType(documentType, downloadDocumentAs);

  if (documentType === DownloadDocumentType.HOLDING_STATEMENT) {
    triggerHoldingStatementDownload(
      injector,
      formatDate(startDate),
      formatDate(endDate),
      documentTemplateType,
      downloadDocumentAs,
      flutterWebView,
      CTAButtonKey,
    );
  } else if (documentType === DownloadDocumentType.SAVINGS_BANK_STATEMENT) {
    triggerSavingsBankStatementDownload(
      injector,
      formatDate(startDate),
      formatDate(endDate),
      documentTemplateType,
      downloadDocumentAs,
      store.selectSnapshot(SavingsAccountState.getCurrentAccountId),
      flutterWebView,
      CTAButtonKey,
    );
  } else {
    triggerBankStatementDownload(
      injector,
      formatDate(startDate),
      formatDate(endDate),
      documentTemplateType,
      downloadDocumentAs,
      accountId,
      flutterWebView,
      CTAButtonKey,
    );
  }
};

const handleManualDateSelection = (
  injector: Injector,
  widgetAttributes: UiZoneWidgetAttributeConfig,
  store: Store,
  documentType: DownloadDocumentType,
  accountId: string,
  downloadDocumentAs: string,
  flutterWebView: unknown,
  CTAButtonKey: string,
): void => {
  switch (documentType) {
    case DownloadDocumentType.BANK_STATEMENT:
    case DownloadDocumentType.HOLDING_STATEMENT:
      handleBankStatement(
        injector,
        documentType,
        accountId,
        downloadDocumentAs,
        flutterWebView,
        CTAButtonKey,
      );
      break;
    case DownloadDocumentType.SAVINGS_BANK_STATEMENT:
      handleSavingsBankStatement(injector, accountId, downloadDocumentAs, flutterWebView, CTAButtonKey);
      break;
    case DownloadDocumentType.PROOF_OF_PAYMENT:
      handleProofOfPayment(
        injector,
        widgetAttributes,
        store,
        accountId,
        downloadDocumentAs,
        flutterWebView,
        CTAButtonKey,
      );
      break;
    case DownloadDocumentType.PROOF_OF_EXCHANGE:
      handleProofOfExchange(
        injector,
        widgetAttributes,
        store,
        downloadDocumentAs,
        flutterWebView,
        CTAButtonKey,
      );
      break;
  }
};

const handleBankStatement = async (
  injector: Injector,
  statementType: DownloadDocumentType.BANK_STATEMENT | DownloadDocumentType.HOLDING_STATEMENT,
  accountId: string,
  downloadDocumentAs: string,
  flutterWebView: unknown,
  CTAButtonKey: string,
): Promise<void> => {
  const downloadDocumentService = injector.get(DownloadDocumentService);
  const alertHandlerService = injector.get(AlertHandlerService);

  const dateRange = downloadDocumentService.dateRange();

  if (!dateRange?.startDate && !dateRange?.endDate) {
    alertHandlerService.showAlertFn('error', 'Please select a date range to download document.');
    return;
  }

  const documentTemplateType = getDocumentTemplateType(statementType, downloadDocumentAs);

  if (statementType === DownloadDocumentType.HOLDING_STATEMENT) {
    triggerHoldingStatementDownload(
      injector,
      dateRange.startDate || '',
      dateRange.endDate || '',
      documentTemplateType,
      downloadDocumentAs,
      flutterWebView,
      CTAButtonKey,
    );
  } else {
    triggerBankStatementDownload(
      injector,
      dateRange.startDate || '',
      dateRange.endDate || '',
      documentTemplateType,
      downloadDocumentAs,
      accountId,
      flutterWebView,
      CTAButtonKey,
    );
  }
};

const handleSavingsBankStatement = async (
  injector: Injector,
  accountId: string,
  downloadDocumentAs: string,
  flutterWebView: unknown,
  CTAButtonKey: string,
): Promise<void> => {
  const downloadDocumentService = injector.get(DownloadDocumentService);
  const alertHandlerService = injector.get(AlertHandlerService);
  const store = injector.get(Store);
  accountId = store.selectSnapshot(SavingsAccountState.getCurrentAccountId);
  const dateRange = downloadDocumentService.dateRange();

  if (!dateRange?.startDate && !dateRange?.endDate) {
    alertHandlerService.showAlertFn('error', 'Please select a date range to download document.');
    return;
  }

  const documentTemplateType = getDocumentTemplateType(
    DownloadDocumentType.SAVINGS_BANK_STATEMENT,
    downloadDocumentAs,
  );

  triggerSavingsBankStatementDownload(
    injector,
    dateRange.startDate || '',
    dateRange.endDate || '',
    documentTemplateType,
    downloadDocumentAs,
    accountId,
    flutterWebView,
    CTAButtonKey,
  );
};

const handleProofOfPayment = (
  injector: Injector,
  widgetAttributes: UiZoneWidgetAttributeConfig,
  store: Store,
  accountId: string,
  downloadDocumentAs: string,
  flutterWebView: unknown,
  CTAButtonKey: string,
): void => {
  const transactionData = getTransactionData(widgetAttributes, store);
  const documentTemplateType = getDocumentTemplateType(
    DownloadDocumentType.PROOF_OF_PAYMENT,
    downloadDocumentAs,
  );

  triggerPaymentProofDownload(
    injector,
    documentTemplateType,
    transactionData.id,
    transactionData.type,
    downloadDocumentAs,
    accountId,
    flutterWebView,
    CTAButtonKey,
  );
};

const handleProofOfExchange = (
  injector: Injector,
  widgetAttributes: UiZoneWidgetAttributeConfig,
  store: Store,
  downloadDocumentAs: string,
  flutterWebView: unknown,
  CTAButtonKey: string,
): void => {
  const transactionData = getTransactionData(widgetAttributes, store);
  const documentTemplateType = getDocumentTemplateType(
    DownloadDocumentType.PROOF_OF_EXCHANGE,
    downloadDocumentAs,
  );

  triggerExchangeProofDownload(
    injector,
    documentTemplateType,
    transactionData.id,
    transactionData.type,
    downloadDocumentAs,
    flutterWebView,
    CTAButtonKey,
  );
};

const getTransactionData = (widgetAttributes: UiZoneWidgetAttributeConfig, store: Store): TransactionData => {
  const isTransaction =
    widgetAttributes.widgetProperties?.['stateContext'] === DocumentStateContext.TRANSACTION_CONTEXT;

  if (isTransaction) {
    const currentTransactionData = store.selectSnapshot(TransactionState.getTransactions);
    return {
      id: currentTransactionData.currentTransactionId,
      type: currentTransactionData.currentTransactionType,
    };
  }

  const notificationData = store.selectSnapshot(NotificationState.getNotification);
  return {
    id: notificationData.selectedNotification.id,
    type: notificationData.selectedNotification.notificationType,
  };
};

type DateRangeConfig = {
  months: number;
  formDataKey: { start: string; end: string };
};

const getDateRangeConfig = (timeInterval: DocumentMonthsUpdate): DateRangeConfig | undefined => {
  const dateRangeConfig: Partial<Record<DocumentMonthsUpdate, DateRangeConfig>> = {
    [DocumentMonthsUpdate.LAST_MONTH]: {
      months: 1,
      formDataKey: { start: 'lastMonthStartDate', end: 'lastMonthEndDate' },
    },
    [DocumentMonthsUpdate.PREVIOUS_3_MONTHS]: {
      months: 3,
      formDataKey: { start: 'lastThreeMonthsStartDate', end: 'lastThreeMonthsEndDate' },
    },
    [DocumentMonthsUpdate.PREVIOUS_6_MONTHS]: {
      months: 6,
      formDataKey: { start: 'lastSixMonthsStartDate', end: 'lastSixMonthsEndDate' },
    },
    [DocumentMonthsUpdate.USER_DEFINED]: {
      months: 0,
      formDataKey: { start: '', end: '' },
    },
  };

  return dateRangeConfig[timeInterval];
};

export const calculateAndSetDateRange = (
  timeInterval: DocumentMonthsUpdate,
  store: Store,
): { startDate: Date; endDate: Date } => {
  const config = getDateRangeConfig(timeInterval);
  const startDate = dayjs()
    .subtract(config?.months || 0, 'months')
    .startOf('month')
    .toDate();
  const endDate = dayjs().subtract(1, 'months').endOf('month').toDate();

  // Check existing dates to avoid redundant store updates
  const existingState = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormActionTypeEnum.PROFILE_BANK_STATEMENT),
  );

  // Extract existing dates
  const existingStartDate = existingState?.formData?.[config?.formDataKey.start || ''];
  const existingEndDate = existingState?.formData?.[config?.formDataKey.end || ''];

  // Only dispatch if dates are different
  const isSameStartDate = existingStartDate?.getTime?.() === startDate.getTime();
  const isSameEndDate = existingEndDate?.getTime?.() === endDate.getTime();

  if (!isSameStartDate || !isSameEndDate) {
    store.dispatch(
      new SetFormActionWithId(
        {
          type: '',
          formData: {
            [config?.formDataKey.start || '']: startDate,
            [config?.formDataKey.end || '']: endDate,
          },
        },
        FormActionTypeEnum.PROFILE_BANK_STATEMENT,
      ),
    );
  }

  return { startDate, endDate };
};

const triggerSavingsBankStatementDownload = (
  injector: Injector,
  startDate: string,
  endDate: string,
  documentTemplateType: DownloadDocumentTypeKey,
  downloadDocumentAs: string,
  accountId: string,
  flutterWebView: unknown,
  CTAButtonKey: string,
): void => {
  const downloadDocumentService = injector.get(DownloadDocumentService);

  triggerDocumentDownloadWithCtaSignal(
    injector,
    CTAButtonKey,
    downloadDocumentService.downloadSavingsBankDocument(
      accountId,
      documentTemplateType,
      startDate,
      endDate,
      downloadDocumentAs,
      flutterWebView,
    ),
    'triggerSavingsBankStatementDownload: Error downloading savings bank statement:',
  );
};

const triggerSavingsAnnualStatementDownload = (
  injector: Injector,
  startDate: string,
  endDate: string,
  documentTemplateType: DownloadDocumentTypeKey,
  downloadDocumentAs: string,
  accountId: string,
  flutterWebView: unknown,
  CTAButtonKey: string,
): void => {
  const downloadDocumentService = injector.get(DownloadDocumentService);
  const store = injector.get(Store);
  const currency = store.selectSnapshot(AccountState.getCurrentMultiCurrencyCode) || undefined;

  triggerDocumentDownloadWithCtaSignal(
    injector,
    CTAButtonKey,
    downloadDocumentService.downloadBankDocument(
      accountId,
      documentTemplateType,
      startDate,
      endDate,
      downloadDocumentAs,
      flutterWebView,
      currency,
    ),
    'triggerSavingsAnnualStatementDownload: Error downloading savings annual statement:',
  );
};

const triggerBankStatementDownload = (
  injector: Injector,
  startDate: string,
  endDate: string,
  documentTemplateType: DownloadDocumentTypeKey,
  downloadDocumentAs: string,
  accountId: string,
  flutterWebView: unknown,
  CTAButtonKey: string,
): void => {
  const downloadDocumentService = injector.get(DownloadDocumentService);
  const store = injector.get(Store);
  const currency = store.selectSnapshot(AccountState.getCurrentMultiCurrencyCode) || undefined;

  triggerDocumentDownloadWithCtaSignal(
    injector,
    CTAButtonKey,
    downloadDocumentService.downloadBankDocument(
      accountId,
      documentTemplateType,
      startDate,
      endDate,
      downloadDocumentAs,
      flutterWebView,
      currency,
    ),
    'triggerBankStatementDownload: Error downloading bank statement:',
  );
};

const triggerHoldingStatementDownload = (
  injector: Injector,
  startDate: string,
  endDate: string,
  documentTemplateType: DownloadDocumentTypeKey,
  downloadDocumentAs: string,
  flutterWebView: unknown,
  CTAButtonKey: string,
): void => {
  const downloadDocumentService = injector.get(DownloadDocumentService);

  triggerDocumentDownloadWithCtaSignal(
    injector,
    CTAButtonKey,
    downloadDocumentService.downloadHoldingDocument(
      documentTemplateType,
      startDate,
      endDate,
      downloadDocumentAs,
      flutterWebView,
    ),
    'triggerHoldingStatementDownload: Error downloading holding statement:',
  );
};

const triggerPaymentProofDownload = (
  injector: Injector,
  documentTemplateType: DownloadDocumentTypeKey,
  transactionId: string,
  transactionType: string,
  downloadDocumentAs: string,
  accountId: string,
  flutterWebView: unknown,
  CTAButtonKey: string,
): void => {
  const downloadDocumentService = injector.get(DownloadDocumentService);

  triggerDocumentDownloadWithCtaSignal(
    injector,
    CTAButtonKey,
    downloadDocumentService.downloadPaymentProof(
      accountId,
      documentTemplateType,
      transactionId,
      transactionType,
      downloadDocumentAs,
      flutterWebView,
    ),
    'triggerPaymentProofDownload: Error downloading payment proof:',
  );
};

const triggerExchangeProofDownload = (
  injector: Injector,
  documentTemplateType: DownloadDocumentTypeKey,
  exchangeId: string,
  transactionType: string,
  downloadDocumentAs: string,
  flutterWebView: unknown,
  CTAButtonKey: string,
): void => {
  const downloadDocumentService = injector.get(DownloadDocumentService);

  triggerDocumentDownloadWithCtaSignal(
    injector,
    CTAButtonKey,
    downloadDocumentService.downloadExchangeProof(
      documentTemplateType,
      exchangeId,
      transactionType,
      downloadDocumentAs,
      flutterWebView,
    ),
    'triggerExchangeProofDownload: Error downloading exchange proof:',
  );
};

const setCtaButtonSignal = (injector: Injector, ctaButtonKey: string, isEnabled: boolean): void => {
  const ctaButtonSignalService = injector.get(CtaButtonSignalService);
  ctaButtonSignalService.setSignal({
    [ctaButtonKey]: isEnabled,
  });
};

const triggerDocumentDownloadWithCtaSignal = (
  injector: Injector,
  ctaButtonKey: string,
  downloadRequest$: Observable<unknown>,
  errorMessage: string,
): void => {
  setCtaButtonSignal(injector, ctaButtonKey, false);

  downloadRequest$.pipe(take(1)).subscribe({
    next: () => {
      setCtaButtonSignal(injector, ctaButtonKey, true);
    },
    error: (error) => {
      setCtaButtonSignal(injector, ctaButtonKey, true);
      console.error(errorMessage, error);
    },
  });
};

const handleKeyProductInformation = (
  injector: Injector,
  downloadDocumentAs: string,
  flutterWebView: unknown,
  CTAButtonKey: string,
): void => {
  const store = injector.get(Store);
  const provider = store.selectSnapshot(ProjectSettingsState.getProjectServiceProvider);

  if (provider !== PaymentProvider.SAASCADA && provider !== PaymentProvider.UNIVERSAL_SANDBOX) return;

  const selectedAccount = store.selectSnapshot(SavingsAccountState.getCurrentAccount);
  const productCode = (
    selectedAccount?.metadata?.['saascada'] as { productIssue?: { code?: string } } | undefined
  )?.productIssue?.code;

  const resolveAndDownload = (products: AvailableSavingsProductType[]) => {
    const matchedProduct = products.find((p) => p.productCode === productCode);
    const kpiUrl = matchedProduct?.documents?.keyProductInformationUrl;
    if (kpiUrl) {
      triggerDirectUrlDownload(injector, kpiUrl, downloadDocumentAs, flutterWebView, CTAButtonKey);
    }
  };

  const availableProducts = store.selectSnapshot(SavingsProductsState.getAvailableProducts);

  if (availableProducts.length > 0) {
    resolveAndDownload(availableProducts);
    return;
  }

  store
    .dispatch(new GetAvailableSavingsProducts())
    .pipe(take(1))
    .subscribe({
      next: () => {
        const freshProducts = store.selectSnapshot(SavingsProductsState.getAvailableProducts);
        resolveAndDownload(freshProducts);
      },
      error: (error) => {
        console.error('handleKeyProductInformation: Error fetching savings products:', error);
      },
    });
};

const triggerDirectUrlDownload = (
  injector: Injector,
  url: string,
  downloadDocumentAs: string,
  flutterWebView: unknown,
  CTAButtonKey: string,
): void => {
  const downloadDocumentService = injector.get(DownloadDocumentService);

  triggerDocumentDownloadWithCtaSignal(
    injector,
    CTAButtonKey,
    downloadDocumentService.downloadFromDirectUrl(url, downloadDocumentAs, flutterWebView),
    'triggerDirectUrlDownload: Error downloading document from direct URL:',
  );
};

const formatDate = (date: Date): string => {
  return dayjs(date).format('YYYY-MM-DD');
};
